import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Youtube } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SEO from "@/components/SEO";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", phone: "" });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      login(data.token, data.user);
      toast.success("Account created!");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <SEO title="Create account | NewStarAds" description="Create your NewStarAds client account to manage campaigns and reports." />
      <div className="hidden lg:flex flex-col justify-between bg-slate-950 text-white p-12">
        <Link to="/" className="flex items-center gap-2 font-display font-extrabold text-xl"><span className="grid place-items-center w-8 h-8 rounded-lg bg-primary"><Youtube className="w-5 h-5" /></span> NewStarAds</Link>
        <div><h2 className="font-display text-4xl font-bold tracking-tight leading-tight">Start growing on YouTube — the honest way.</h2><p className="mt-4 text-slate-400">Create an account to request campaigns and track progress.</p></div>
        <p className="text-xs text-slate-500">© {new Date().getFullYear()} NewStarAds</p>
      </div>
      <div className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="register-form">
          <div><h1 className="font-display text-3xl font-bold tracking-tight">Create account</h1><p className="mt-1 text-slate-500 text-sm">Join NewStarAds as a client.</p></div>
          <div className="space-y-1.5"><Label>Full name</Label><Input data-testid="register-name" value={form.name} onChange={set("name")} required /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input data-testid="register-email" type="email" value={form.email} onChange={set("email")} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Company</Label><Input data-testid="register-company" value={form.company} onChange={set("company")} /></div>
            <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="register-phone" value={form.phone} onChange={set("phone")} /></div>
          </div>
          <div className="space-y-1.5"><Label>Password</Label><Input data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} /></div>
          <Button type="submit" disabled={loading} className="w-full h-11 rounded-full" data-testid="register-submit">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create account"}</Button>
          <div className="flex items-center gap-3 text-xs text-slate-400"><span className="flex-1 h-px bg-slate-200" /> or <span className="flex-1 h-px bg-slate-200" /></div>
          <GoogleSignInButton label="Sign up with Google" />
          <p className="text-sm text-slate-500 text-center">Have an account? <Link to="/login" className="text-primary font-medium">Log in</Link></p>
        </form>
      </div>
    </div>
  );
}
